import { Box, Checkbox, Flex, Spinner, VStack } from "@chakra-ui/react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useSavedProfile } from "@equidefi/portals/hooks/useSavedProfiles";
import { blankIfNA, governmentIdLabel } from "@equidefi/shared";
import { GOVERNMENT_ID } from "@equidefi/shared";
import { Heading, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";

import { ProfileNavButton, VaultContainer } from "@/pages/app/vault/v2/components";
import type { DTO } from "@equidefi/shared/types/dto";

type SavedProfile = DTO.SavedProfile;
type SavedProfileSigner = DTO.SavedProfileSigner;

const EDIT_SECTIONS = {
  PERSONAL_DETAILS: "personal-details",
  GOVERNMENT_ID: "government-id",
  HOME_ADDRESS: "home-address",
  ACCOUNT_TYPE: "account-type",
  ACCREDITATION: "accreditation",
} as const;

const SavedProfileView = () => {
  useDocumentTitle(["Saved Profile"]);

  const { id } = useParams<{ id: string }>();
  if (!id) {
    throw new Error("Saved profile id is required.");
  }

  const navigate = useNavigate();
  const navigateEdit = (section: string) => {
    navigate(`/vault/profile/saved-profiles/${id}/edit/${section}`);
  };

  const [showTaxId, setShowTaxId] = useState(false);
  const [showEin, setShowEin] = useState(false);

  const { data: profile, isLoading } = useSavedProfile(id);

  if (isLoading) {
    return (
      <VaultContainer>
        <Flex justify="center" py="10">
          <Spinner />
        </Flex>
      </VaultContainer>
    );
  }

  if (!profile) {
    return (
      <VaultContainer>
        <ProfileNavButton to="/vault/profile/saved-profiles" layout="rtl">
          Back To Profiles
        </ProfileNavButton>
      </VaultContainer>
    );
  }

  const displayName = getDisplayName(profile);
  const accountLabel = getAccountLabel(profile);

  return (
    <VaultContainer>
      <VStack align="stretch" spacing="4" w="full">
        <ProfileNavButton to="/vault/profile/saved-profiles" layout="rtl">
          Back To Profiles
        </ProfileNavButton>

        <Box bg="#F4EDFF" border="1px solid" borderColor="#805AD5" borderRadius="lg" p="4" textAlign="center">
          <Text m="0" mb="3" fontSize="12px" lineHeight="16px" fontWeight="700" textTransform="uppercase">
            {accountLabel}
          </Text>

          <Heading fontSize="20px" lineHeight="24px" fontWeight="700">
            {displayName}
          </Heading>
        </Box>

        <Flex gap="3" bg="#EAF0FF" border="1px solid" borderColor="#315DE8" borderRadius="lg" p="4">
          <Flex
            align="center"
            justify="center"
            flexShrink="0"
            w="5"
            h="5"
            borderRadius="full"
            bg="#315DE8"
            color="white"
            fontSize="12px"
            fontWeight="700">
            i
          </Flex>

          <Box>
            <Text m="0" mb="2" fontSize="15px" lineHeight="20px" fontWeight="700">
              Updating An Investment Profile
            </Text>

            <Text m="0" fontSize="15px" lineHeight="22px">
              Updates to this information will not apply to currently open investments, only to new investments.
            </Text>
          </Box>
        </Flex>

        <Section title="Personal Details" onEdit={() => navigateEdit(EDIT_SECTIONS.PERSONAL_DETAILS)}>
          <Field label="First Name" value={profile.first_name} />
          <Field label="Middle Name" value={profile.middle_name || "(not set)"} muted={!profile.middle_name} />
          <Field label="Last Name" value={profile.last_name} />

          <Divider />

          <Field label="Phone" value={profile.phone_number} />

          <Divider />

          <Field label="Citizenship" value={profile.citizenship} />
          <Field label="Date of Birth" value={formatDate(profile.birthdate)} />

          <Divider />

          <Flex align="center" justify="space-between" gap="3">
            <Field
              label="SSN/TIN"
              value={showTaxId ? profile.tax_id || profile.ein : maskValue(profile.tax_id || profile.ein)}
            />

            <Checkbox isChecked={showTaxId} onChange={() => setShowTaxId((prev) => !prev)}>
              <Text m="0" fontSize="13px" lineHeight="18px">
                Show SSN/TIN
              </Text>
            </Checkbox>
          </Flex>
        </Section>

        <Section title="Government ID" onEdit={() => navigateEdit(EDIT_SECTIONS.GOVERNMENT_ID)}>
          <Field label="ID Type" value={governmentIdLabel(profile.identification_type)} />
          <Field label="ID Number" value={profile.identification_number} />

          <Divider />

          <Field label="Issuing Authority" value={profile.issuing_authority} />
          <Field label="ID Issue Date" value={formatDate(profile.id_issue_date)} />
          <Field label="ID Expiry Date" value={formatDate(profile.id_expire_date)} />
        </Section>

        <Section title="Home Address" onEdit={() => navigateEdit(EDIT_SECTIONS.HOME_ADDRESS)}>
          <Address profile={profile} />
        </Section>

        <AccountDetailsSection
          profile={profile}
          showEin={showEin}
          onToggleEin={() => setShowEin((prev) => !prev)}
          onEdit={() => navigateEdit(EDIT_SECTIONS.ACCOUNT_TYPE)}
        />

        {profile.signers?.length ? (
          <Section title="Signers">
            <VStack align="stretch" spacing="4">
              {profile.signers.map((signer, index) => (
                <Box key={signer.id || index}>
                  <Flex justify="space-between" gap="3">
                    <Box>
                      <Field label="Name" value={getSignerName(signer)} />
                      <Field label="Phone" value={signer.phone_number} />
                    </Box>

                    <EditButton
                      onClick={() => navigate(`/vault/profile/saved-profiles/${id}/edit/signers/${signer.id || index}`)}
                    />
                  </Flex>

                  {index < (profile?.signers?.length || 0) - 1 && <Divider />}
                </Box>
              ))}
            </VStack>
          </Section>
        ) : null}

        <Section title="Accreditation" onEdit={() => navigateEdit(EDIT_SECTIONS.ACCREDITATION)}>
          <Field
            label="Accreditation Documents"
            value={`${profile.accreditation_files?.length || 0} file${profile.accreditation_files?.length === 1 ? "" : "s"}`}
          />
          <Field label="Address Verification Documents" value={profile.utility_bill ? "1 file" : "0 files"} />

          <Divider />

          <Field label="Accredited Investor (self reported)" value={profile.accredited ? "Yes" : "No"} />
          <Field label="Broker-Dealer Affiliate" value={profile.broker_dealer ? "Yes" : "No"} />

          <Divider />

          <Field label="Net Worth" value={formatMoney(profile.net_worth)} />
          <Field label="Annual Income" value={formatMoney(profile.annual_income)} />
        </Section>
      </VStack>
    </VaultContainer>
  );
};

const Section = ({
  title,
  children,
  onEdit,
}: {
  title: string;
  children: React.ReactNode;
  onEdit?: () => void;
}) => {
  return (
    <Box
      bg="white"
      borderRadius="lg"
      p="4"
      boxShadow="0 1px 2px rgba(0, 0, 0, 0.2)"
      border="1px solid"
      borderColor="gray.100">
      <Flex align="center" justify="space-between" mb="4">
        <Heading fontSize="17px" lineHeight="24px" fontWeight="700">
          {title}
        </Heading>

        {onEdit && <EditButton onClick={onEdit} />}
      </Flex>

      {children}
    </Box>
  );
};

const EditButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Flex
      as="button"
      type="button"
      align="center"
      gap="2"
      color="#2F55D4"
      onClick={onClick}
      fontSize="15px"
      lineHeight="20px">
      <Icon.Edit size="24px" />
      Edit
    </Flex>
  );
};

const Field = ({
  label,
  value,
  muted,
  locked,
}: {
  label: string;
  value?: React.ReactNode;
  muted?: boolean;
  locked?: boolean;
}) => {
  return (
    <Text m="0" fontSize="13px" lineHeight="22px" color={muted ? "#657797" : "black"}>
      <Box as="span" fontWeight="700" color="black">
        {label}:
      </Box>{" "}
      {value || "—"}
      {locked && (
        <Flex display="inline-flex" pos="relative" ml="1.5" top="0.5" left="0.5" color="#657797">
          <Icon.Lock size="16px" />
        </Flex>
      )}
    </Text>
  );
};

const Divider = () => {
  return <Box h="1px" bg="gray.200" my="4" />;
};

const Address = ({ profile }: { profile: SavedProfile }) => {
  return (
    <Box>
      <Text m="0" fontSize="13px" lineHeight="20px">
        {profile.address_street_1}
      </Text>

      {profile.address_street_2 && (
        <Text m="0" fontSize="13px" lineHeight="20px">
          {profile.address_street_2}
        </Text>
      )}

      <Text m="0" fontSize="13px" lineHeight="20px">
        {profile.address_city}
      </Text>

      <Text m="0" fontSize="13px" lineHeight="20px">
        {profile.address_state} {blankIfNA(profile.address_postal_code)}
      </Text>

      <Text m="0" fontSize="13px" lineHeight="20px">
        {profile.address_country_code}
      </Text>
    </Box>
  );
};

const EntityAddress = ({ profile }: { profile: SavedProfile }) => {
  return (
    <Box mt="2">
      <Text m="0" fontSize="13px" lineHeight="20px">
        {profile.entity_address_street_1}
      </Text>

      {profile.entity_address_street_2 && (
        <Text m="0" fontSize="13px" lineHeight="20px">
          {profile.entity_address_street_2}
        </Text>
      )}

      <Text m="0" fontSize="13px" lineHeight="20px">
        {profile.entity_address_city}
      </Text>

      <Text m="0" fontSize="13px" lineHeight="20px">
        {profile.entity_address_state} {blankIfNA(profile.entity_address_postal_code)}
      </Text>

      <Text m="0" fontSize="13px" lineHeight="20px">
        {profile.entity_address_country_code}
      </Text>
    </Box>
  );
};

const AccountDetailsSection = ({
  profile,
  showEin,
  onToggleEin,
  onEdit,
}: {
  profile: SavedProfile;
  showEin: boolean;
  onToggleEin: () => void;
  onEdit: () => void;
}) => {
  const accountType = profile.account_type;

  if (accountType === "ENTITY") {
    return (
      <Section title="Entity Details" onEdit={onEdit}>
        <Field label="Type" value="Entity" muted locked />
        <Field label="Sub-type" value={profile.ta_sub_type} />
        <Field label="Company Name" value={profile.company} />

        <Flex align="center" justify="space-between" gap="3">
          <Field label="EIN" value={showEin ? profile.ein : maskValue(profile.ein)} />
          <Checkbox isChecked={showEin} onChange={onToggleEin}>
            <Text m="0" fontSize="13px" lineHeight="18px">
              Show EIN
            </Text>
          </Checkbox>
        </Flex>

        <Divider />

        <Field label="Company Address" value={null} />
        <EntityAddress profile={profile} />
      </Section>
    );
  }

  if (accountType === "TRUST") {
    return (
      <Section title="Trust Details" onEdit={onEdit}>
        <Field label="Type" value="Trust" muted locked />
        <Field label="Sub-type" value={profile.ta_sub_type} />
        <Field label="Trust Name" value={profile.company} />

        <Flex align="center" justify="space-between" gap="3">
          <Field label="EIN" value={showEin ? profile.ein : maskValue(profile.ein)} />
          <Checkbox isChecked={showEin} onChange={onToggleEin}>
            <Text m="0" fontSize="13px" lineHeight="18px">
              Show EIN
            </Text>
          </Checkbox>
        </Flex>

        <Divider />

        <Field label="Trust Address" value={null} />
        <EntityAddress profile={profile} />
      </Section>
    );
  }

  // Individuals have no sub-types, so there is nothing to edit.
  const canEdit = accountType !== "INDIVIDUAL";

  return (
    <Section title="Account Type" onEdit={canEdit ? onEdit : undefined}>
      <Field label="Type" value={formatAccountType(profile.account_type)} muted locked />
      <Divider />
      <Field label="Sub-type" value={profile.ta_sub_type} />
    </Section>
  );
};

const getDisplayName = (profile: SavedProfile) => {
  if (profile.company) return profile.company;
  return [profile.first_name, profile.last_name].filter(Boolean).join(" ");
};

const getSignerName = (signer: SavedProfileSigner) => {
  const fullName = [signer.first_name, signer.last_name].filter(Boolean).join(" ");
  return fullName || "Unnamed Signer";
};

const getAccountLabel = (profile: SavedProfile) => {
  const type = profile.account_type;
  if (type === "INDIVIDUAL") return "Individual Account";
  if (type === "JOINT") return "Joint Account";
  if (type === "TRUST") return "Trust Account";
  if (type === "ENTITY") return "Entity Account";
  return "Investment Account";
};

const formatAccountType = (value?: string) => {
  if (!value) return undefined;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const formatIdType = (value?: string) => {
  if (!value) return undefined;
  return GOVERNMENT_ID[value]?.label ?? value;
};

const formatDate = (value?: string) => {
  if (!value) return undefined;
  return new Date(value).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const formatMoney = (value?: string) => {
  if (!value) return undefined;
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

const maskValue = (value?: string) => {
  if (!value) return undefined;
  return "*".repeat(8);
};

export default SavedProfileView;
